import { useState, useEffect, useCallback, useMemo } from 'react';
import { BookOpen, Save, RefreshCw, ChevronDown, ChevronRight, Users, Shield, User, Search, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Knowledge categories configuration
const CATEGORIES = [
  { id: 'guest', label: 'Gast-Wissen', icon: User, color: 'bg-green-600', borderColor: 'border-green-600' },
  { id: 'employee', label: 'Mitarbeiter-Wissen', icon: Users, color: 'bg-blue-600', borderColor: 'border-blue-600' },
  { id: 'admin', label: 'Admin-Wissen', icon: Shield, color: 'bg-purple-600', borderColor: 'border-purple-600' }
];

export const KnowledgePage = ({ user }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [selectedEntryId, setSelectedEntryId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const loadKnowledge = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('gb-sylt-user');
      let authHeader = {};
      if (token) {
        try {
          const parsed = JSON.parse(token);
          if (parsed.token) {
            authHeader = { Authorization: `Bearer ${parsed.token}` };
          }
        } catch (e) {}
      }
      
      const response = await axios.get(`${API}/knowledge/admin`, { headers: authHeader });
      
      if (response.data.entries && response.data.entries.length > 0) {
        // Ensure all categories have entries 1-6
        const loadedEntries = response.data.entries;
        const completeEntries = [];
        
        CATEGORIES.forEach(cat => {
          for (let i = 1; i <= 6; i++) {
            const existing = loadedEntries.find(e => e.id === `${cat.id}-${i}` || (e.category === cat.id && e.order === i));
            if (existing) {
              completeEntries.push({ ...existing, id: `${cat.id}-${i}`, order: i });
            } else {
              completeEntries.push({
                id: `${cat.id}-${i}`,
                category: cat.id,
                title: '',
                content: '',
                order: i
              });
            }
          }
        });
        
        setEntries(completeEntries);
      } else {
        // Initialize default entries
        const defaultEntries = [];
        CATEGORIES.forEach(cat => {
          for (let i = 1; i <= 6; i++) {
            defaultEntries.push({
              id: `${cat.id}-${i}`,
              category: cat.id,
              title: '',
              content: '',
              order: i
            });
          }
        });
        setEntries(defaultEntries);
      }
      setHasChanges(false);
    } catch (error) {
      console.error('Error loading knowledge:', error);
      setMessage({ type: 'error', text: 'Fehler beim Laden des Wissens' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKnowledge();
  }, [loadKnowledge]);

  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      return () => clearTimeout(timer);
    }
  }, [message.text]);

  // Get the current selected entry from entries array (always in sync)
  const selectedEntry = useMemo(() => {
    if (!selectedEntryId) return null;
    return entries.find(e => e.id === selectedEntryId) || null;
  }, [entries, selectedEntryId]);

  const handleEntryChange = (entryId, field, value) => {
    setEntries(prev => prev.map(entry => 
      entry.id === entryId ? { ...entry, [field]: value } : entry
    ));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (saving) return;
    
    try {
      setSaving(true);
      
      const token = localStorage.getItem('gb-sylt-user');
      let authHeader = {};
      if (token) {
        try {
          const parsed = JSON.parse(token);
          if (parsed.token) {
            authHeader = { Authorization: `Bearer ${parsed.token}` };
          }
        } catch (e) {}
      }
      
      // Filter out empty entries before saving
      const entriesToSave = entries.filter(e => e.title || e.content);
      
      await axios.put(`${API}/knowledge`, { entries: entriesToSave }, { headers: authHeader });
      
      setMessage({ type: 'success', text: 'Wissen erfolgreich gespeichert!' });
      setHasChanges(false);
      
    } catch (error) {
      console.error('Error saving:', error);
      setMessage({ type: 'error', text: 'Fehler beim Speichern: ' + (error.response?.data?.detail || error.message) });
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (categoryId) => {
    if (expandedCategory === categoryId) {
      setExpandedCategory(null);
      setSelectedEntryId(null);
    } else {
      setExpandedCategory(categoryId);
      setSelectedEntryId(null);
    }
  };

  const getEntriesForCategory = (categoryId) => {
    return entries
      .filter(e => e.category === categoryId)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .slice(0, 6);
  };

  // Search: filter + highlight logic
  const isSearching = searchQuery.trim().length > 0;
  const searchLower = searchQuery.trim().toLowerCase();

  const filteredResults = useMemo(() => {
    if (!isSearching) return null;
    const results = {};
    let totalCount = 0;
    CATEGORIES.forEach(cat => {
      const catEntries = entries
        .filter(e => e.category === cat.id)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      const matched = catEntries.filter(e => {
        const title = (e.title || '').toLowerCase();
        const content = (e.content || '').toLowerCase();
        return title.includes(searchLower) || content.includes(searchLower);
      });
      if (matched.length > 0) {
        results[cat.id] = matched;
        totalCount += matched.length;
      }
    });
    return { byCategory: results, total: totalCount };
  }, [entries, searchLower, isSearching]);

  // Highlight component
  const HighlightText = ({ text, query }) => {
    if (!query || query.length === 0 || !text) return <>{text}</>;
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
    return (
      <>
        {parts.map((part, i) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <mark key={i} className="bg-yellow-300/80 text-gray-900 rounded-sm px-0.5">{part}</mark>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </>
    );
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-100">
      <div className="max-w-5xl mx-auto p-4 lg:p-6 space-y-4">
        
        {/* Header */}
        <div className="bg-white rounded-lg border p-4 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-orange-600 flex items-center justify-center">
              <BookOpen className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Wissensverwaltung</h1>
              <p className="text-sm text-gray-500">Memo-Felder für Samira bearbeiten</p>
            </div>
          </div>
          
          {/* Save Button */}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors ${
              hasChanges 
                ? 'bg-green-600 text-white hover:bg-green-700' 
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
            }`}
            data-testid="save-knowledge-btn"
          >
            {saving ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? 'Speichere...' : 'Speichern'}
          </button>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg border p-3 sticky top-0 z-10" data-testid="knowledge-search-bar">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Suche im Wissen..."
              className="w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-colors"
              data-testid="knowledge-search-input"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors"
                data-testid="knowledge-search-clear"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {isSearching && filteredResults && (
            <p className="text-xs text-gray-500 mt-2 pl-1" data-testid="knowledge-search-count">
              {filteredResults.total === 0
                ? 'Keine passenden Einträge gefunden'
                : `${filteredResults.total} ${filteredResults.total === 1 ? 'Ergebnis' : 'Ergebnisse'} in ${Object.keys(filteredResults.byCategory).length} ${Object.keys(filteredResults.byCategory).length === 1 ? 'Bereich' : 'Bereichen'}`
              }
            </p>
          )}
        </div>

        {/* Message */}
        {message.text && (
          <div 
            className={`p-4 rounded-lg border flex items-center justify-between ${
              message.type === 'error' ? 'bg-red-100 text-red-800 border-red-200' :
              message.type === 'success' ? 'bg-green-100 text-green-800 border-green-200' :
              'bg-blue-100 text-blue-800 border-blue-200'
            }`}
            data-testid="message-box"
          >
            <span>{message.text}</span>
            <button 
              type="button" 
              onClick={() => setMessage({ type: '', text: '' })} 
              className="ml-4 p-1 hover:opacity-70 text-lg font-bold"
            >
              ×
            </button>
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="bg-white rounded-lg border p-8 text-center">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400 mb-2" />
            <p className="text-gray-500">Lade Wissen...</p>
          </div>
        ) : isSearching ? (
          /* Search Results View */
          filteredResults && filteredResults.total === 0 ? (
            <div className="bg-white rounded-lg border p-8 text-center" data-testid="knowledge-no-results">
              <Search className="h-10 w-10 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">Keine passenden Einträge gefunden</p>
              <p className="text-gray-400 text-sm mt-1">Versuchen Sie einen anderen Suchbegriff</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="knowledge-search-results">
              {CATEGORIES.map((category) => {
                const CategoryIcon = category.icon;
                const matchedEntries = filteredResults?.byCategory[category.id];
                if (!matchedEntries || matchedEntries.length === 0) return null;
                
                return (
                  <div key={category.id} className="bg-white rounded-lg border overflow-hidden">
                    {/* Category Header (auto-expanded) */}
                    <div className={`px-4 py-3 flex items-center gap-3 border-b bg-gray-50`}>
                      <div className={`h-8 w-8 rounded-lg ${category.color} flex items-center justify-center flex-shrink-0`}>
                        <CategoryIcon className="h-4 w-4 text-white" />
                      </div>
                      <p className="font-semibold text-gray-900 text-sm">{category.label}</p>
                      <span className="text-xs text-gray-400">{matchedEntries.length} Treffer</span>
                    </div>
                    
                    {/* Matching Entries */}
                    <div className="divide-y divide-gray-100">
                      {matchedEntries.map((entry) => (
                        <div key={entry.id} className="p-4" data-testid={`search-result-${entry.id}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded ${category.color} text-white`}>
                              #{entry.order}
                            </span>
                            {entry.title && (
                              <h3 className="font-medium text-gray-900 text-sm">
                                <HighlightText text={entry.title} query={searchQuery.trim()} />
                              </h3>
                            )}
                          </div>
                          {entry.content && (
                            <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed mt-1 max-h-80 overflow-y-auto bg-gray-50 rounded-lg p-3 border border-gray-100">
                              <HighlightText text={entry.content} query={searchQuery.trim()} />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          /* Normal Accordion Categories */
          <div className="space-y-2">
            {CATEGORIES.map((category) => {
              const CategoryIcon = category.icon;
              const isExpanded = expandedCategory === category.id;
              const categoryEntries = getEntriesForCategory(category.id);
              const filledCount = categoryEntries.filter(e => e.title || e.content).length;
              
              return (
                <div key={category.id} className="bg-white rounded-lg border overflow-hidden">
                  {/* Category Header */}
                  <button
                    type="button"
                    onClick={() => toggleCategory(category.id)}
                    className={`w-full px-4 py-4 flex items-center gap-3 text-left transition-colors hover:bg-gray-50 ${
                      isExpanded ? 'border-b' : ''
                    }`}
                    data-testid={`category-${category.id}`}
                  >
                    <div className={`h-10 w-10 rounded-lg ${category.color} flex items-center justify-center flex-shrink-0`}>
                      <CategoryIcon className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{category.label}</p>
                      <p className="text-sm text-gray-500">{filledCount} von 6 Einträgen befüllt</p>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  
                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="flex min-h-[400px]">
                      {/* Entry List - Left Side */}
                      <div className="w-48 border-r bg-gray-50 flex-shrink-0">
                        {categoryEntries.map((entry) => {
                          const isSelected = selectedEntryId === entry.id;
                          const hasContent = entry.title || entry.content;
                          return (
                            <button
                              key={entry.id}
                              type="button"
                              onClick={() => setSelectedEntryId(entry.id)}
                              className={`w-full px-4 py-3 text-left border-b border-gray-200 last:border-b-0 transition-colors ${
                                isSelected 
                                  ? `${category.color} text-white` 
                                  : 'hover:bg-gray-100'
                              }`}
                              data-testid={`entry-${entry.id}`}
                            >
                              <div className="flex items-center gap-2">
                                <span className={`font-medium ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                                  #{entry.order}
                                </span>
                                {hasContent && !isSelected && (
                                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                )}
                              </div>
                              {entry.title && (
                                <p className={`text-xs truncate mt-0.5 ${isSelected ? 'text-white/80' : 'text-gray-500'}`}>
                                  {entry.title}
                                </p>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      
                      {/* Entry Editor - Right Side */}
                      <div className="flex-1 p-4">
                        {selectedEntry ? (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Titel / Betreff
                              </label>
                              <input
                                type="text"
                                value={selectedEntry.title || ''}
                                onChange={(e) => handleEntryChange(selectedEntry.id, 'title', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                placeholder={`Titel für ${category.label} #${selectedEntry.order}`}
                                data-testid="entry-title-input"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Memo-Feld (Markdown erlaubt)
                              </label>
                              <textarea
                                value={selectedEntry.content || ''}
                                onChange={(e) => handleEntryChange(selectedEntry.id, 'content', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                                rows={12}
                                placeholder={`## Frage\nAntwort: Hier steht die Antwort...\n---\n## Weitere Frage\nAntwort: ...`}
                                data-testid="entry-content-textarea"
                              />
                              <p className="text-xs text-gray-400 mt-1">
                                Tipp: Verwende ## für Überschriften, --- für Trenner zwischen Themen
                              </p>
                            </div>
                            {hasChanges && (
                              <p className="text-sm text-orange-600">
                                ⚠️ Ungespeicherte Änderungen - bitte "Speichern" klicken
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="h-full flex items-center justify-center text-gray-400 py-12">
                            <div className="text-center">
                              <CategoryIcon className="h-12 w-12 mx-auto mb-2 opacity-30" />
                              <p>Wählen Sie einen Eintrag (#1-#6) aus der Liste</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Info Box */}
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-sm text-orange-800">
          <strong>Hinweis zur Struktur:</strong>
          <ul className="list-disc list-inside mt-2 space-y-1 text-orange-700">
            <li>Klicken Sie auf einen Wissensbereich, um die Einträge #1 bis #6 anzuzeigen</li>
            <li>Wählen Sie einen Eintrag aus der linken Liste zum Bearbeiten</li>
            <li>Vergessen Sie nicht auf <strong>Speichern</strong> zu klicken!</li>
          </ul>
        </div>

        {/* Summary */}
        {!loading && (
          <div className="text-center text-sm text-gray-500">
            Gesamt: {entries.filter(e => e.title || e.content).length} Einträge befüllt
            {hasChanges && <span className="text-orange-600 ml-2">• Ungespeicherte Änderungen</span>}
          </div>
        )}

        {/* Floating Save Button (mobile) */}
        {hasChanges && !loading && (
          <div className="fixed bottom-20 right-4 lg:hidden">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="h-14 w-14 rounded-full bg-green-600 text-white shadow-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
            >
              {saving ? (
                <RefreshCw className="h-6 w-6 animate-spin" />
              ) : (
                <Save className="h-6 w-6" />
              )}
            </button>
          </div>
        )}

      </div>
    </div>
  );
};
